// Phase 3.1c — Multiple-inventory Product Details drawer.
//
// Replaces the antd <Drawer> + antd <Collapse>/<Panel> + MUI <RadioGroup> +
// antd <Modal>/<InputNumber> with a shadcn <Sheet> + <Accordion>. Presentation
// only — every piece of state and every handler owned by the parent is still
// passed through unchanged, even though most are now unused here (see below),
// so the parent's wiring doesn't need to change.
//
// Sold, Damaged and Stock Adjustment are all read-only in this sheet (product
// decision) — no status-change radios, no add/remove controls. Because of
// that, `popupOpen`, `addDamagedStatus`/`setAddDamagedStatus`,
// `updateMultipleInventoryData`/`setUpdateMultipleInventoryData`,
// `updateMultipleInventoryStatus`, `closeDamagedStatus`, `modalDamaged`,
// `onDamagedConfirm` and `onDamagedCancel` are all unused in this component
// now; left in the props contract rather than touching the parent's wiring.
import * as React from "react";
import moment from "moment";
import { Pencil } from "lucide-react";
import AppConstant from "../../Globals/AppConstants";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "ui/sheet";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "ui/accordion";
import { ConfirmDialog } from "../../shared/confirm-dialog";
import { useDispatch, useSelector } from "react-redux";
import { getComboItemsAction } from "../../Store/actions/apparelAction";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "ui/table";

export interface MultiInventorySheetProps {
  open: boolean;
  onClose: () => void;
  product: any;
  counts: any;
  soldList: any;
  damagedList: any[];
  scrapList: any[];
  stockAdjustmentList: any[];
  // controlled expand state (legacy arrays: ['1'] open, [] closed)
  collapse: any;
  setCollapse: (v: any) => void;
  damagedCollapse: any;
  setDamagedCollapse: (v: any) => void;
  scrapCollapse: any;
  setScrapCollapse: (v: any) => void;
  stockAdjustmentCollapse: any;
  setStockAdjustmentCollapse: (v: any) => void;
  // status change handlers (all parent-owned, unchanged)
  popupOpen: (key: any, item: any) => void;
  modalOpen: boolean;
  onModalConfirm: () => void;
  onModalCancel: () => void;
  addDamagedStatus: boolean;
  setAddDamagedStatus: (v: boolean) => void;
  addScrapStatus: boolean;
  setAddScrapStatus: (v: boolean) => void;
  updateMultipleInventoryData: any;
  setUpdateMultipleInventoryData: (v: any) => void;
  updateMultipleInventoryStatus: (key: any, item: any) => void;
  closeDamagedStatus: (item: any) => void;
  modalDamaged: boolean;
  onDamagedConfirm: () => void;
  onDamagedCancel: () => void;
  // when provided, shows an Edit button in the header that opens the edit form
  onEdit?: (product: any) => void;
}

// Bridge the legacy Collapse activeKey arrays to Accordion's single-value model.
const isOpen = (state: any) => (Array.isArray(state) ? state.includes("1") : !!state);
const toState = (open: boolean) => (open ? ["1"] : []);

// Stock Adjustment shows this many rows before offering "View N more".
const ADJUSTMENT_PREVIEW_COUNT = 4;

export function MultiInventorySheet({
  open,
  onClose,
  product,
  counts,
  soldList,
  damagedList,
  scrapList,
  stockAdjustmentList,
  collapse,
  setCollapse,
  damagedCollapse,
  setDamagedCollapse,
  scrapCollapse,
  setScrapCollapse,
  stockAdjustmentCollapse,
  setStockAdjustmentCollapse,
  popupOpen,
  modalOpen,
  onModalConfirm,
  onModalCancel,
  addDamagedStatus,
  setAddDamagedStatus,
  addScrapStatus,
  setAddScrapStatus,
  updateMultipleInventoryData,
  setUpdateMultipleInventoryData,
  updateMultipleInventoryStatus,
  closeDamagedStatus,
  modalDamaged,
  onDamagedConfirm,
  onDamagedCancel,
  onEdit,
}: MultiInventorySheetProps) {
  const availableCount = counts?.availableCount;
  const [showAllAdjustments, setShowAllAdjustments] = React.useState(false);

  const dispatch = useDispatch();
  const { getComboItemsLoad, getComboItemsData, getComboItemsFor } = useSelector(
    (state: any) => state.ApparelReducerState
  );
  const isCombo = Number(product?.stockType ?? 1) === 2;
  const comboLines: any[] =
    isCombo && getComboItemsFor === product?.productDetailsId && Array.isArray(getComboItemsData)
      ? getComboItemsData
      : [];

  React.useEffect(() => {
    if (open && isCombo && product?.productDetailsId) {
      dispatch(getComboItemsAction({ productDetailId: product.productDetailsId }));
    }
  }, [open, isCombo, product?.productDetailsId, dispatch]);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >

        <SheetHeader>
          <div className="flex items-center gap-2 pr-8">
            <SheetTitle>
              {AppConstant?.productId} -{" "}
              <span className="text-muted-foreground">{product?.productId}</span>
            </SheetTitle>
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(product)}
                aria-label="Edit product"
                title="Edit"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-4 mz">
          {/* --- product summary --- */}
          <div className="rounded-xl border p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm font-semibold text-foreground">{product?.productName}</div>
              <div className="shrink-0 text-sm font-semibold tabular-nums text-foreground">₹ {product?.mrp}</div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                {product?.categoryName}
              </span>
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                {product?.subCategoryName}
              </span>
            </div>
          </div>

          {/* --- Combo Items (combo stock only, read-only) --- */}
          {isCombo && (
            <div className="rounded-xl border p-4">
              <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">
                Combo Items
                <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                  {comboLines.length}
                </span>
              </div>
              {getComboItemsLoad && comboLines.length === 0 ? (
                <div className="text-sm text-muted-foreground">Loading combo items...</div>
              ) : comboLines.length === 0 ? (
                <div className="text-sm text-muted-foreground">No combo items found.</div>
              ) : (
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="h-auto px-2 py-2">Item</TableHead>
                      <TableHead className="h-auto w-[84px] px-2 py-2 text-right">Qty / combo</TableHead>
                      <TableHead className="h-auto w-[84px] px-2 py-2 text-right">Available</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comboLines.map((line: any) => (
                      <TableRow key={line?.productDetailId}>
                        <TableCell className="min-w-0 break-words px-2 py-2">
                          <div className="font-medium">{line?.productId ?? "-"}</div>
                          <div className="text-xs text-muted-foreground">{line?.productName}</div>
                        </TableCell>
                        <TableCell className="px-2 py-2 text-right tabular-nums">{line?.quantity}</TableCell>
                        <TableCell className="px-2 py-2 text-right tabular-nums">{line?.availableCount ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {/* --- stat row: Available / Sold / Damaged at a glance --- */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-xl border p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Total</div>
              <div className="mt-1 text-xl font-bold tabular-nums text-foreground">{counts?.totalCount ?? counts?.itemCount}</div>
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {AppConstant?.available}
              </div>
              <div className="mt-1 text-xl font-bold tabular-nums text-foreground">{availableCount}</div>
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sold</div>
              <div className="mt-1 text-xl font-bold tabular-nums text-foreground">{counts?.soldCount}</div>
            </div>
            <div className="rounded-xl border p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Damaged</div>
              <div
                className={`mt-1 text-xl font-bold tabular-nums ${counts?.damagedCount ? "text-destructive" : "text-muted-foreground"
                  }`}
              >
                {counts?.damagedCount}
              </div>
            </div>
          </div>

          {/* --- Sold --- */}
          <Accordion
            type="single"
            collapsible
            value={isOpen(collapse) ? "1" : ""}
            onValueChange={(v) => setCollapse(toState(!!v))}
          >
            <AccordionItem value="1" className="rounded-xl border px-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  Sold
                  <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                    {counts?.soldCount}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-2">
                {(soldList?.invoices || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No sold items recorded</div>
                ) : (
                  (soldList?.invoices || []).map((item: any, i: number) => {
                    // One entry per return type present on this invoice.
                    const returns = [
                      { label: "Normal", qty: Number(item?.normalReturnQuantity) || 0, className: "bg-green-500/10 text-green-600" },
                      { label: "Damaged", qty: Number(item?.damagedReturnQuantity) || 0, className: "bg-destructive/10 text-destructive" },
                      { label: "Scrap", qty: Number(item?.scrapReturnQuantity) || 0, className: "bg-destructive/10 text-destructive" },
                    ].filter((r) => r.qty > 0);
                    const returnedTotal = returns.reduce((sum, r) => sum + r.qty, 0);
                    return (
                      <div key={i} className="rounded-lg border bg-muted/30 p-3 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <div className="font-medium">
                            Invoice ID -{" "}
                            <span className="text-muted-foreground">{item?.invoiceId}</span>
                          </div>
                          <span className="shrink-0 rounded-full bg-background px-2.5 py-1 text-xs font-bold tabular-nums text-foreground">
                            Qty {item?.itemQuantity}
                          </span>
                        </div>
                        {returns.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                            <span>Returned: {returnedTotal}</span>
                            {returns.map((r) => (
                              <span
                                key={r.label}
                                className={`rounded-full px-2.5 py-0.5 font-semibold ${r.className}`}
                              >
                                {returns.length > 1 ? `${r.label}: ${r.qty}` : `${r.label} Return`}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* --- Damaged --- */}
          <Accordion
            type="single"
            collapsible
            value={isOpen(damagedCollapse) ? "1" : ""}
            onValueChange={(v) => setDamagedCollapse(toState(!!v))}
          >
            <AccordionItem value="1" className="rounded-xl border px-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  Damaged
                  <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                    {counts?.damagedCount}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3">
                {/* Read-only — Damaged is view-only, no add/remove from this sheet. */}
                {(damagedList || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No damaged items recorded</div>
                ) : (
                  (damagedList || []).map((item: any, i: number) => {
                    let x = 0;
                    return (
                      <div key={i}>
                        <div className="rounded-lg border p-3">
                          <div className="text-sm font-medium">
                            {item?.invoiceId ? `Invoice ID - ${item?.invoiceId}` : ""}
                          </div>
                          <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                            <div>Items</div>
                            <div>Updated On</div>
                          </div>
                        </div>
                        {(item?.data || []).map((itemx: any, j: number) =>
                          (new Array(itemx?.damagedCount).fill(0) || []).map((_z: any, k: number) => (
                            <div
                              key={`${j}-${k}`}
                              className="flex items-center justify-between gap-3 border-b px-3 py-2 text-sm"
                            >
                              <div className="w-5 tabular-nums text-muted-foreground">{++x}</div>
                              <div className="flex-1">
                                {itemx?.firstName + " " + (itemx?.lastName ? itemx?.lastName : "")} -{" "}
                                {moment.utc(itemx?.updatedOn).local().format("DD-MMM-YYYY / hh:mm A")}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    );
                  })
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* --- Stock Adjustment (view-only) --- */}
          <Accordion
            type="single"
            collapsible
            value={isOpen(stockAdjustmentCollapse) ? "1" : ""}
            onValueChange={(v) => setStockAdjustmentCollapse(toState(!!v))}
          >
            <AccordionItem value="1" className="rounded-xl border px-3">
              <AccordionTrigger className="hover:no-underline">
                <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  Stock Adjustment
                  <span className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-muted-foreground">
                    {(stockAdjustmentList || []).length}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {(stockAdjustmentList || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground">No stock adjustments recorded.</div>
                ) : (
                  <>
                    <div className="divide-y">
                      {(showAllAdjustments
                        ? stockAdjustmentList || []
                        : (stockAdjustmentList || []).slice(0, ADJUSTMENT_PREVIEW_COUNT)
                      ).map((item: any, i: number) => (
                        <div key={i} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                          <div>
                            <div className="font-semibold text-foreground">{item?.createdByName || "—"}</div>
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {item?.createdOn
                                ? moment.utc(item.createdOn).local().format("DD MMM YYYY · hh:mm A")
                                : "-"}
                            </div>
                            {item?.remarks && (
                              <div className="mt-0.5 text-xs text-muted-foreground">{item.remarks}</div>
                            )}
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${Number(item?.quantity) >= 0
                                ? "bg-emerald-50 text-emerald-600"
                                : "bg-red-50 text-destructive"
                              }`}
                          >
                            {Number(item?.quantity) > 0 ? `+${item.quantity}` : item.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                    {(stockAdjustmentList || []).length > ADJUSTMENT_PREVIEW_COUNT && (
                      <button
                        type="button"
                        className="mt-2 text-xs font-semibold text-primary hover:underline"
                        onClick={() => setShowAllAdjustments((v) => !v)}
                      >
                        {showAllAdjustments
                          ? "Show less"
                          : `View ${(stockAdjustmentList || []).length - ADJUSTMENT_PREVIEW_COUNT} more`}
                      </button>
                    )}
                  </>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* --- Scrap ---
          <Accordion
            type="single"
            collapsible
            value={isOpen(scrapCollapse) ? "1" : ""}
            onValueChange={(v) => setScrapCollapse(toState(!!v))}
          >
            <AccordionItem value="1">
              <div className="flex items-center justify-between gap-2">
                <AccordionTrigger className="flex-1">Scrap - {counts?.scrapCount}</AccordionTrigger>
                <div className="shrink-0">
                  {!addScrapStatus ? (
                    <Button
                      variant="link"
                      className="h-auto p-0"
                      onClick={() => setAddScrapStatus(true)}
                    >
                      {availableCount != 0 ? "Add" : ""}
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Item</span>
                      <Input
                        type="number"
                        min={0}
                        max={availableCount}
                        className="h-8 w-20"
                        onChange={(e) =>
                          setUpdateMultipleInventoryData({
                            ...updateMultipleInventoryData,
                            scrapCount: e.target.value === "" ? null : Number(e.target.value),
                          })
                        }
                      />
                      <Button
                        variant="link"
                        className="h-auto p-0"
                        onClick={() => setAddScrapStatus(false)}
                      >
                        {AppConstant?.cancel}
                      </Button>
                      <Button size="sm" onClick={() => updateMultipleInventoryStatus(6, "")}>
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <AccordionContent className="space-y-3">
                {(scrapList || []).map((item: any, i: number) => {
                  let x = 0;
                  return (
                    <div key={i}>
                      <div className="rounded-lg border p-3">
                        <div className="text-sm font-medium">
                          {item?.invoiceId ? `Invoice ID - ${item?.invoiceId}` : ""}
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                          <div>Items</div>
                          <div>Updated On</div>
                        </div>
                      </div>
                      {(item?.data || []).map((itemx: any, j: number) =>
                        (new Array(itemx?.scrapCount).fill(0) || []).map((_z: any, k: number) => (
                          <div
                            key={`${j}-${k}`}
                            className="flex items-center justify-between gap-3 border-b px-3 py-2 text-sm"
                          >
                            <div className="w-5 tabular-nums text-muted-foreground">{++x}</div>
                            <div className="flex-1">
                              {itemx?.firstName + " " + (itemx?.lastName ? itemx?.lastName : "")} -{" "}
                              {moment.utc(itemx?.updatedOn).local().format("DD-MMM-YYYY / hh:mm A")}
                            </div>
                            <div
                              className="cursor-pointer text-muted-foreground hover:text-foreground"
                              onClick={() => closeDamagedStatus(item)}
                            >
                              X
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                })}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          */}
        </div>

        {/* Sold status-change confirm (legacy modalOpen) */}
        <ConfirmDialog
          open={modalOpen}
          onOpenChange={(o) => { if (!o) onModalCancel(); }}
          title="Do you want to Change the Status"
          confirmLabel="OK"
          onConfirm={onModalConfirm}
        />

        {/* Remove damaged/scrap item confirm (legacy modalDamaged) */}
        <ConfirmDialog
          open={modalDamaged}
          onOpenChange={(o) => { if (!o) onDamagedCancel(); }}
          title="Do you want to Remove the Item"
          confirmLabel="OK"
          destructive
          onConfirm={onDamagedConfirm}
        />
      </SheetContent>
    </Sheet>
  );
}

export default MultiInventorySheet;
